import React from 'react';
import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

export interface PremisePayload {
  premiseText: string;
  order: number;
}

export type SerializedPremiseNode = Spread<
  { premiseText: string; order: number },
  SerializedLexicalNode
>;

function PremiseComponent({
  premiseText,
  order,
}: {
  premiseText: string;
  order: number;
  nodeKey: string;
}) {
  return (
    <div
      style={{
        position: 'relative',
        backgroundColor: '#fafafa',
        borderLeft: '3px solid #d1d5db',
        borderRadius: '0 8px 8px 0',
        padding: '14px 20px 14px 16px',
        margin: '16px 0',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '10px',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: '#e5e7eb',
            color: '#6b7280',
            fontSize: '12px',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {order}
        </span>
        <div>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              color: '#9ca3af',
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}
          >
            Premise
          </span>
          <div style={{ color: '#4b5563', lineHeight: 1.6, fontSize: '15px', marginTop: '4px' }}>
            {premiseText}
          </div>
        </div>
      </div>
    </div>
  );
}

export class PremiseNode extends DecoratorNode<React.ReactElement> {
  __premiseText: string;
  __order: number;

  static getType(): string {
    return 'premise';
  }

  static clone(node: PremiseNode): PremiseNode {
    return new PremiseNode(node.__premiseText, node.__order, node.__key);
  }

  constructor(premiseText: string, order: number, key?: NodeKey) {
    super(key);
    this.__premiseText = premiseText;
    this.__order = order;
  }

  static importJSON(serialized: SerializedPremiseNode): PremiseNode {
    return new PremiseNode(serialized.premiseText, serialized.order);
  }

  exportJSON(): SerializedPremiseNode {
    return {
      type: 'premise',
      version: 1,
      premiseText: this.__premiseText,
      order: this.__order,
    };
  }

  createDOM(): HTMLElement {
    const div = document.createElement('div');
    div.className = 'premise-node';
    return div;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): React.ReactElement {
    return (
      <PremiseComponent
        premiseText={this.__premiseText}
        order={this.__order}
        nodeKey={this.__key}
      />
    );
  }
}

export function $createPremiseNode(payload: PremisePayload): PremiseNode {
  return new PremiseNode(payload.premiseText, payload.order);
}

export function $isPremiseNode(node: LexicalNode): node is PremiseNode {
  return node instanceof PremiseNode;
}
